import { createInstance } from 'i18next';
import { createResourceBackend } from './createBackend.js';

const DEFAULT_SUPPORTED = ['en', 'es'];
const DEFAULT_LANGUAGE_STORAGE_KEY = 'aimsos.i18n.language';

function readStoredLanguage(storageKey, supportedLngs) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw && supportedLngs.includes(raw)) return raw;
  } catch {
    /* private mode / blocked storage */
  }
  return null;
}

function writeStoredLanguage(storageKey, lng) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, lng);
  } catch {
    /* ignore */
  }
}

/**
 * Creates an isolated i18next instance + registerNamespace bound to that instance’s loaders.
 * Each Vite app (shell, standalone module) should call this once at bootstrap.
 *
 * @param {object} [options]
 * @param {string} [options.lng='es'] Initial language when nothing is stored (or persistence off)
 * @param {string} [options.fallbackLng='en'] Fallback when a key or bundle is missing
 * @param {string[]} [options.supportedLngs]
 * @param {boolean} [options.persistLanguage=true] Save / restore UI language in localStorage
 * @param {string} [options.languageStorageKey='aimsos.i18n.language'] localStorage key (same origin = shared across apps)
 * @param {string} [options.defaultNS='common']
 * @param {string[]} [options.initialNamespaces] Namespaces to load on init (must be registered before init)
 * @param {Record<string, Record<string, () => Promise<unknown>>>} [options.registrations] Namespace -> lng -> dynamic import
 * @param {object} [options.i18next] Extra i18next init options
 */
export function createI18n(options = {}) {
  const {
    lng = 'en',
    fallbackLng = 'es',
    supportedLngs = DEFAULT_SUPPORTED,
    persistLanguage = true,
    languageStorageKey = DEFAULT_LANGUAGE_STORAGE_KEY,
    defaultNS = 'common',
    initialNamespaces = [defaultNS],
    registrations = {},
    i18next: i18nextOverrides = {},
  } = options;

  const storedLng = persistLanguage ? readStoredLanguage(languageStorageKey, supportedLngs) : null;
  const initialLng = storedLng ?? lng;

  const namespaceLoaders = new Map();

  const backend = createResourceBackend(namespaceLoaders, { fallbackLng });
  const i18n = createInstance();

  function registerNamespace(namespace, languageImporters) {
    if (!namespace || typeof languageImporters !== 'object') {
      throw new Error(
        '[createI18n] registerNamespace(namespace, languageImporters) expects an object of lng -> () => import(...)'
      );
    }
    namespaceLoaders.set(namespace, languageImporters);
  }

  for (const [ns, importers] of Object.entries(registrations)) {
    registerNamespace(ns, importers);
  }

  i18n.use(backend).init({
    lng: initialLng,
    fallbackLng,
    supportedLngs,
    load: 'languageOnly',
    ns: initialNamespaces,
    defaultNS,
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    ...i18nextOverrides,
  });

  if (persistLanguage) {
    i18n.on('languageChanged', (nextLng) => {
      if (supportedLngs.includes(nextLng)) {
        writeStoredLanguage(languageStorageKey, nextLng);
      }
    });
  }

  return { i18n, registerNamespace };
}
